/**
 * İşlem geçmişi alan diff'i için metin karşılaştırma yardımcısı.
 *
 * Uzun değerlerde (URL, açıklama, alt yazı) "neydi → ne oldu" gösterimi tek başına yetmiyor:
 * iki uzun metin yan yana basıldığında farkın nerede olduğu gözle bulunamıyor. Bu modül,
 * iki değerin ortak ön ve son ekini ayırıp yalnız **değişen orta parçayı** işaretlenebilir
 * hale getirir.
 *
 * @module ArticleOrderMedia/historyDiff
 */

/**
 * Vurgulamanın anlamlı sayılması için gereken en az ortak karakter oranı.
 * Bunun altında iki değer "farklı şeyler" demektir (ör. `google` → `pubmed`); ortak harfleri
 * işaretlemek fark aramayı kolaylaştırmak yerine gürültü üretir, o yüzden vurgulama yapılmaz.
 */
const MIN_SHARED_RATIO = 0.35;

/**
 * İki metnin baştan kaç karakteri ortak.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
const commonPrefixLength = (a, b) => {
  const max = Math.min(a.length, b.length);
  let index = 0;
  while (index < max && a[index] === b[index]) index += 1;
  return index;
};

/**
 * İki metnin sondan kaç karakteri ortak. `skip`, önek olarak zaten sayılmış karakterlerdir —
 * aynı karakterin hem önek hem sonek sayılıp parçaların çakışmasını engeller
 * (ör. `abc` → `abcabc`).
 * @param {string} a
 * @param {string} b
 * @param {number} skip - Önek uzunluğu
 * @returns {number}
 */
const commonSuffixLength = (a, b, skip) => {
  const max = Math.min(a.length, b.length) - skip;
  let index = 0;
  while (index < max && a[a.length - 1 - index] === b[b.length - 1 - index]) index += 1;
  return index;
};

/**
 * İki değeri ortak önek / değişen orta / ortak sonek olarak üçe böler.
 *
 * Vurgulama yapılmayacak durumlarda `null` döner — çağıran taraf o zaman değerleri
 * olduğu gibi basar:
 * - değerlerden biri boşsa ya da ikisi eşitse (gösterilecek fark yok),
 * - ortak karakter oranı `MIN_SHARED_RATIO`'nun altındaysa (tamamen farklı değerler).
 *
 * @param {string} before - Eski değer (görünen metin hâli)
 * @param {string} after - Yeni değer (görünen metin hâli)
 * @returns {{ before: { head: string, mid: string, tail: string }, after: { head: string, mid: string, tail: string } } | null}
 * @example
 * splitByCommonAffix('wikipedi', 'wikipedia')
 * // → { before: { head: 'wikipedi', mid: '', tail: '' }, after: { head: 'wikipedi', mid: 'a', tail: '' } }
 */
export const splitByCommonAffix = (before, after) => {
  const a = String(before ?? '');
  const b = String(after ?? '');
  if (!a || !b || a === b) return null;

  const prefix = commonPrefixLength(a, b);
  const suffix = commonSuffixLength(a, b, prefix);
  const shared = prefix + suffix;
  if (shared < Math.min(a.length, b.length) * MIN_SHARED_RATIO) return null;

  const cut = (text) => ({
    head: text.slice(0, prefix),
    mid: text.slice(prefix, text.length - suffix),
    tail: suffix ? text.slice(text.length - suffix) : '',
  });

  return { before: cut(a), after: cut(b) };
};
